import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { AudioBars } from './AudioBars';
import { SoundOrb } from './SoundOrb';

export function VoiceAgent() {
  const room = useRoomContext();
  const [agentState, setAgentState] = useState('connecting');
  const [agentMST,   setAgentMST]   = useState(null);
  const [audioOn,    setAudioOn]     = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!room) return;

    const onSub = (track, _pub, participant) => {
      if (!participant.isLocal && track.kind === 'audio') {
        setAgentMST(track.mediaStreamTrack);
        setAgentState('speaking');
        if (audioRef.current) {
          audioRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
          audioRef.current.play().catch(() => {});
        }
      }
    };
    const onUnsub = (track, _pub, participant) => {
      if (!participant.isLocal && track.kind === 'audio') {
        setAgentMST(null);
        setAgentState('listening');
      }
    };

    room.remoteParticipants.forEach(p =>
      p.audioTrackPublications.forEach(pub => {
        if (pub.track?.mediaStreamTrack) {
          setAgentMST(pub.track.mediaStreamTrack);
          setAgentState('speaking');
        }
      })
    );

    room.on(RoomEvent.TrackSubscribed,   onSub);
    room.on(RoomEvent.TrackUnsubscribed, onUnsub);
    return () => {
      room.off(RoomEvent.TrackSubscribed,   onSub);
      room.off(RoomEvent.TrackUnsubscribed, onUnsub);
    };
  }, [room]);

  const activateAudio = useCallback(async () => {
    if (room) await room.startAudio().catch(() => {});
    if (audioRef.current) audioRef.current.play().catch(() => {});
    setAudioOn(true);
    setAgentState('listening');
  }, [room]);

  const cfg = {
    connecting: { label: 'Conectando...',  color: '#6b7280', pulse: false },
    listening:  { label: 'Escuchando',     color: '#3b82f6', pulse: true  },
    thinking:   { label: 'Procesando...',  color: '#f59e0b', pulse: true  },
    speaking:   { label: 'Respondiendo',   color: '#22c55e', pulse: true  },
  }[agentState] || { label: 'Conectando...', color: '#6b7280', pulse: false };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0a0a0a', color: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      gap: '28px',
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: 0 }}>
          Nova<span style={{ color: '#3b82f6' }}>Cast</span>
          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '1rem', marginLeft: 12 }}>
            | Novasys University
          </span>
        </h1>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '4px 0 0' }}>
          Asistente de admisiones
        </p>
      </div>

      {/* Esfera 3D animada */}
      <SoundOrb
        audioTrack={agentMST}
        agentState={agentState}
        size={200}
      />

      {/* Visualizador de barras */}
      <AudioBars
        audioTrack={agentMST}
        isActive={agentState === 'speaking'}
        color={cfg.color}
      />

      {/* Badge estado */}
      <div style={{
        background: '#1f2937', border: `1px solid ${cfg.color}44`,
        color: cfg.color, padding: '6px 20px', borderRadius: '999px',
        fontSize: '0.85rem', fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: cfg.color,
          animation: cfg.pulse ? 'pulse 1.5s infinite' : 'none',
          display: 'inline-block',
        }} />
        {cfg.label}
      </div>

      {/* Botón activar */}
      {!audioOn && (
        <button onClick={activateAudio} style={{
          background: '#2563eb', color: '#fff', border: 'none',
          borderRadius: '999px', padding: '14px 36px',
          fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 4px 24px rgba(37,99,235,0.4)',
        }}>
          🎤 Comenzar conversación
        </button>
      )}

      {audioOn && (
        <p style={{ color: '#4b5563', fontSize: '0.8rem' }}>
          Habla para interactuar con el asistente
        </p>
      )}

      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity:1; transform:scale(1); }
          50%       { opacity:0.5; transform:scale(1.3); }
        }
      `}</style>
    </div>
  );
}
